import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Footer } from "@/components/Footer";
import { PageMeta } from "@/components/site/blocks";
import { Band, Closing, Faqs, H3, Hero, In, JsonLd, List, P, ProblemFirst, Related, Src, WhatIs, btn, wrap } from "@/components/seo/kit";

// /tools/auto-reply-generator: the free Inbox tool (gtm/site/free-tools-plan.md section 2, owner's yes
// 2026-10-19). Plan: gtm/products/clearmakes-inbox/seo/pages/tools-auto-reply-generator.md
// Runs fully in the browser: no fetch, no AI, nothing typed leaves the page. The draft is kept in
// this browser's localStorage only. Gmail and Outlook steps checked on 2026-10-22 against the help
// pages linked at the bottom; re-check them in the 90-day refresh.

const PATH = "/tools/auto-reply-generator";
const TITLE = "Free Auto Reply Email Generator (Gmail and Outlook)";
const DESC = "Write an auto reply email in a minute: request received, after hours, out of office or holiday, plus the steps to switch it on in Gmail and Outlook. Free.";
const SIGNUP = "/login?view=signup&utm_source=tool&utm_medium=web&utm_campaign=auto-reply-generator";
const STORE = "cm-auto-reply-generator-v1";

type Situation = "request" | "afterhours" | "ooo" | "holiday" | "busy";
type Fields = { name: string; company: string; hours: string; replyTime: string; backDate: string; urgent: string; extra: string };
type FieldKey = keyof Fields;

const SITUATIONS: { id: Situation; label: string; hint: string; fields: FieldKey[] }[] = [
  { id: "request", label: "Request received", hint: "Say “we got it” and when they'll hear back.", fields: ["company", "replyTime", "urgent", "extra", "name"] },
  { id: "afterhours", label: "After hours", hint: "A message that arrives when you're closed.", fields: ["company", "hours", "replyTime", "urgent", "extra", "name"] },
  { id: "ooo", label: "Out of office", hint: "You're away for a few days.", fields: ["backDate", "urgent", "extra", "name"] },
  { id: "holiday", label: "Holiday closure", hint: "The whole team is closed for a holiday.", fields: ["company", "backDate", "urgent", "extra", "name"] },
  { id: "busy", label: "Busier than usual", hint: "Replies are slower than normal for a while.", fields: ["company", "replyTime", "urgent", "extra", "name"] },
];

const FIELD_INFO: Record<FieldKey, { label: string; hint: string }> = {
  company: { label: "Your business or team", hint: "For example: Northside Freight" },
  hours: { label: "Your working hours", hint: "For example: Monday to Friday, 8am to 6pm Eastern" },
  replyTime: { label: "When they'll hear back", hint: "For example: within one working day" },
  backDate: { label: "Back on", hint: "For example: Monday 3 November" },
  urgent: { label: "If it's urgent (optional)", hint: "For example: call 555 0100" },
  extra: { label: "One more line (optional)", hint: "For example: Please include your order number." },
  name: { label: "Signed by", hint: "For example: Sam, Northside Freight" },
};

const EMPTY: Fields = { name: "", company: "", hours: "", replyTime: "", backDate: "", urgent: "", extra: "" };

/** A filled value, or a bracketed gap the visitor can see and fill in later. */
const v = (s: string, gap: string) => s.trim() || `[${gap}]`;
const sentence = (s: string) => {
  const t = s.trim();
  return !t ? "" : /[.!?]$/.test(t) ? t : `${t}.`;
};

function write(sit: Situation, f: Fields) {
  const who = f.company.trim();
  const sign = v(f.name, "your name or team");
  const urgent = f.urgent.trim() ? `If it's urgent, please ${f.urgent.trim().replace(/\.$/, "")}.` : "";
  const urgentShort = f.urgent.trim() ? ` Urgent? Please ${f.urgent.trim().replace(/\.$/, "")}.` : "";
  const extra = sentence(f.extra);
  const reply = v(f.replyTime, "when they'll hear back");
  const back = v(f.backDate, "the day you're back");
  const para = (...ps: string[]) => ps.filter(Boolean).join("\n\n");

  switch (sit) {
    case "request":
      return {
        subject: "We got your message",
        friendly: para("Hi,", `Thanks for getting in touch${who ? ` with ${who}` : ""}. This is a quick note to say your message reached us, and a person will reply ${reply}.`, extra, urgent, `Thanks,\n${sign}`),
        short: para(`Thanks, we got your message. We'll reply ${reply}.${urgentShort}`, extra, sign),
      };
    case "afterhours":
      return {
        subject: "We got your message (we're closed right now)",
        friendly: para("Hi,", `Thanks for your email. It reached ${who || "us"} outside our working hours, which are ${v(f.hours, "your working hours")}, so nobody is reading it right now.`, `We'll reply ${reply}.`, extra, urgent, `Thanks,\n${sign}`),
        short: para(`Thanks for your email. We're closed right now (our hours: ${v(f.hours, "your working hours")}). We'll reply ${reply}.${urgentShort}`, extra, sign),
      };
    case "ooo":
      return {
        subject: `Out of office until ${back}`,
        friendly: para("Hi,", `Thanks for your email. I'm away until ${back} and won't be reading email until then. I'll reply when I'm back.`, extra, f.urgent.trim() ? `If it can't wait, please ${f.urgent.trim().replace(/\.$/, "")}.` : "", `Thanks,\n${sign}`),
        short: para(`I'm away until ${back} and will reply when I'm back.${urgentShort}`, extra, sign),
      };
    case "holiday":
      return {
        subject: `We're closed until ${back}`,
        friendly: para("Hi,", `Thanks for your email. ${who || "We"} ${who ? "is" : "are"} closed for the holiday, and we're back on ${back}.`, "Your message is safe with us, and we'll answer messages in the order they arrived. You don't need to send it again.", extra, urgent, `Happy holidays,\n${sign}`),
        short: para(`We're closed for the holiday and back on ${back}. We'll reply in the order messages arrived.${urgentShort}`, extra, sign),
      };
    case "busy":
      return {
        subject: "We got your message (replies are a little slower)",
        friendly: para("Hi,", `Thanks for your email. ${who ? `${who} is` : "We're"} getting more messages than usual right now, so replies are taking a little longer.`, `We'll reply ${reply}. You don't need to send it again: your message is in the queue.`, extra, urgent, `Thanks for your patience,\n${sign}`),
        short: para(`Thanks, we got your message. We're busier than usual and will reply ${reply}. No need to send it again.${urgentShort}`, extra, sign),
      };
  }
}

/* ── The set-up steps for each mail app, by situation (checked 2026-10-22) ── */

type HowTo = { title: string; steps: string[]; note?: string };

const GMAIL_VACATION: HowTo = {
  title: "Gmail: the vacation responder",
  steps: [
    "Open Gmail on a computer. Click Settings (the gear), then See all settings.",
    "On the General tab, scroll to Vacation responder and choose Vacation responder on.",
    "Set the First day, and the Last day if you know it. Paste the subject and the message.",
    "Optional: tick the box to send the reply only to people in your Contacts.",
    "Click Save Changes at the bottom.",
  ],
  note: "Gmail sends it once to each sender, and again if they write after four days. Emails in Spam and from mailing lists get no reply.",
};

const GMAIL_TEMPLATE: HowTo = {
  title: "Gmail: a template and a filter",
  steps: [
    "Open Gmail on a computer. Click Settings (the gear), See all settings, then Advanced. Next to Templates, choose Enable, then Save Changes.",
    "Click Compose and paste the message. Click More options (the three dots), Templates, Save draft as template, Save as new template, and give it a name.",
    "Click the search options button in the search box and say which emails get the reply, for example To: your support address.",
    "Click Create filter, tick Send template, choose your template, and click Create filter.",
  ],
  note: "A filter answers every matching email, every time, including people who write twice. Templates work in Gmail on a computer, not in the phone app.",
};

const OUTLOOK_AUTO: HowTo = {
  title: "Outlook: automatic replies",
  steps: [
    "Outlook on the web or Outlook.com: click Settings (the gear), then Account, then Automatic replies. New Outlook for Windows: View, View settings, Accounts, Automatic replies. Classic Outlook: File, Automatic Replies.",
    "Turn on Automatic replies.",
    "Tick Send replies only during a time period and set the start and end, so it switches off by itself.",
    "Paste the message. Optional: Send replies only to contacts. Work accounts can set a separate reply for people outside the organization.",
    "Click Save (OK in classic Outlook).",
  ],
  note: "Built-in automatic replies work with Microsoft 365, Exchange and Outlook.com accounts. A Gmail or other IMAP account added to Outlook needs a rule instead, and Outlook must stay open for it to work.",
};

const OUTLOOK_ALWAYS: HowTo = {
  title: "Outlook: automatic replies left on",
  steps: [
    "Outlook on the web or Outlook.com: click Settings (the gear), then Account, then Automatic replies. New Outlook for Windows: View, View settings, Accounts, Automatic replies.",
    "Turn on Automatic replies, and leave Send replies only during a time period unticked, so it stays on until you turn it off.",
    "Paste the message. Work accounts: put the same message in the reply for people outside your organization, because that's where customers are.",
    "Click Save.",
    "Classic Outlook for Windows, Microsoft 365 or Exchange account, to answer every email instead: File, Manage Rules & Alerts, New Rule, Apply rule on messages I receive, pick which emails, then have server reply using a specific message, write it, Save & Close, Finish.",
  ],
  note: "Work accounts send customers the reply for people outside your organization, which Microsoft says also answers newsletters and junk; Send replies only to contacts avoids that.",
};

const HOWTO: Record<Situation, { gmail: HowTo[]; outlook: HowTo[]; clock?: boolean }> = {
  request: { gmail: [GMAIL_TEMPLATE], outlook: [OUTLOOK_ALWAYS] },
  afterhours: { gmail: [GMAIL_TEMPLATE], outlook: [OUTLOOK_ALWAYS], clock: true },
  ooo: { gmail: [GMAIL_VACATION], outlook: [OUTLOOK_AUTO] },
  holiday: { gmail: [GMAIL_VACATION], outlook: [OUTLOOK_AUTO] },
  busy: { gmail: [GMAIL_TEMPLATE], outlook: [OUTLOOK_ALWAYS] },
};

/* ── Small pieces ─────────────────────────────────────────────────────────── */

const label = "text-[14px] font-medium text-[hsl(0_0%_4%)]";
const input =
  "mt-1.5 block w-full rounded-lg border border-[hsl(216_30%_80%)] bg-[hsl(0_0%_100%)] px-3.5 py-2.5 text-[16px] text-[hsl(0_0%_4%)] placeholder:text-[hsl(215_10%_55%)] focus:border-[hsl(216_84%_52%)] focus:outline-none focus:ring-2 focus:ring-[hsl(216_84%_52%/0.25)]";
const card = "min-w-0 rounded-2xl border border-[hsl(216_20%_86%)] bg-[hsl(0_0%_100%)] p-5 sm:p-6";
const smallBtn =
  "inline-flex h-10 items-center justify-center whitespace-nowrap rounded-lg border border-[hsl(216_30%_80%)] bg-[hsl(0_0%_100%)] px-4 text-[15px] font-medium text-[hsl(0_0%_4%)] transition-colors hover:bg-[hsl(216_60%_97%)]";

const CopyButton = ({ text, what }: { text: string; what: string }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const t = document.createElement("textarea");
      t.value = text;
      document.body.appendChild(t);
      t.select();
      document.execCommand("copy");
      t.remove();
    }
    setDone(true);
    window.setTimeout(() => setDone(false), 1800);
  };
  return (
    <button type="button" onClick={copy} className={smallBtn} aria-label={`Copy the ${what}`}>
      {done ? "Copied" : `Copy ${what}`}
    </button>
  );
};

const HowToBlock = ({ h }: { h: HowTo }) => (
  <div className="mt-4">
    <p className="text-[15.5px] font-semibold text-[hsl(0_0%_4%)]">{h.title}</p>
    <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-[15px] leading-relaxed text-[hsl(215_16%_28%)]">
      {h.steps.map((s) => (
        <li key={s}>{s}</li>
      ))}
    </ol>
    {h.note && <p className="mt-2 text-[14px] leading-relaxed text-[hsl(215_14%_40%)]">{h.note}</p>}
  </div>
);

/** The same steps as the tool shows, as plain page text (so search engines and print read them). */
const HowToSteps = ({ h }: { h: HowTo }) => (
  <>
    <ol className="mt-3 list-decimal space-y-2 pl-6 text-[16.5px] leading-relaxed text-[hsl(215_16%_30%)]">
      {h.steps.map((s) => (
        <li key={s}>{s}</li>
      ))}
    </ol>
    {h.note && <P>{h.note}</P>}
  </>
);

const MessageCard = ({ title, subject, body }: { title: string; subject: string; body: string }) => (
  <div className={card}>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-[16px] font-semibold text-[hsl(0_0%_4%)]">{title}</p>
      <CopyButton text={body} what="message" />
    </div>
    <p className="mt-3 text-[14px] text-[hsl(215_14%_40%)]">
      Subject: <span className="font-medium text-[hsl(0_0%_4%)]">{subject}</span>
    </p>
    <pre className="mt-3 whitespace-pre-wrap break-words rounded-lg bg-[hsl(216_60%_97%)] p-4 font-sans text-[15.5px] leading-relaxed text-[hsl(0_0%_8%)]">{body}</pre>
  </div>
);

/* ── The tool ─────────────────────────────────────────────────────────────── */

const Generator = () => {
  const [sit, setSit] = useState<Situation>("request");
  const [f, setF] = useState<Fields>(EMPTY);
  const [app, setApp] = useState<"gmail" | "outlook">("gmail");
  const [loaded, setLoaded] = useState(false);

  // The draft lives only in this browser. Reading it after the first paint keeps the prerendered HTML the same for everyone.
  useEffect(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(STORE) || "null");
      if (saved && typeof saved === "object") {
        if (SITUATIONS.some((s) => s.id === saved.sit)) setSit(saved.sit);
        if (saved.app === "gmail" || saved.app === "outlook") setApp(saved.app);
        if (saved.f) setF({ ...EMPTY, ...saved.f });
      }
    } catch {
      /* no storage: the tool still works */
    }
    setLoaded(true);
  }, []);
  useEffect(() => {
    if (!loaded) return;
    try {
      window.localStorage.setItem(STORE, JSON.stringify({ sit, app, f }));
    } catch {
      /* ignore */
    }
  }, [sit, app, f, loaded]);

  const current = SITUATIONS.find((s) => s.id === sit)!;
  const out = useMemo(() => write(sit, f), [sit, f]);
  const how = HOWTO[sit];

  const download = () => {
    const steps = [...how.gmail, ...how.outlook]
      .map((h) => `${h.title}\n${h.steps.map((s, i) => `${i + 1}. ${s}`).join("\n")}${h.note ? `\nNote: ${h.note}` : ""}`)
      .join("\n\n");
    const text = `Auto reply: ${current.label}\n\nSubject: ${out.subject}\n\n--- Friendly version ---\n\n${out.friendly}\n\n--- Short version ---\n\n${out.short}\n\n--- How to switch it on ---\n\n${steps}\n\nMade with the free auto reply generator at https://clearmakes.com${PATH}\n`;
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `auto-reply-${sit}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const clear = () => {
    setF(EMPTY);
    try {
      window.localStorage.removeItem(STORE);
    } catch {
      /* ignore */
    }
  };

  return (
    <section id="generator" className="bg-[hsl(216_60%_97%)] py-14 sm:py-20" aria-labelledby="generator-title">
      <div className={wrap}>
        <h2 id="generator-title" className="text-[1.9rem] font-semibold leading-[1.1] tracking-[-0.03em] text-[hsl(0_0%_4%)] sm:text-[2.5rem]">
          Write your auto reply
        </h2>
        <p className="mt-4 max-w-3xl text-[18px] leading-relaxed text-[hsl(215_16%_30%)]">
          Pick the situation and fill in the boxes. The message and the set-up steps change as you type. Nothing you type leaves this page: it is
          kept only in this browser, so a reload doesn't lose it.
        </p>

        <div className="mt-10 grid gap-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
          {/* The form */}
          <div className={card}>
            <fieldset>
              <legend className={label}>What is the reply for?</legend>
              <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {SITUATIONS.map((s) => (
                  <label
                    key={s.id}
                    className={`flex cursor-pointer flex-col rounded-lg border px-3.5 py-2.5 transition-colors ${
                      sit === s.id ? "border-[hsl(216_84%_52%)] bg-[hsl(216_85%_96%)]" : "border-[hsl(216_30%_84%)] hover:bg-[hsl(216_60%_98%)]"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <input type="radio" name="situation" value={s.id} checked={sit === s.id} onChange={() => setSit(s.id)} className="accent-[hsl(216_84%_52%)]" />
                      <span className="text-[15.5px] font-medium text-[hsl(0_0%_4%)]">{s.label}</span>
                    </span>
                    <span className="mt-0.5 pl-6 text-[13.5px] leading-snug text-[hsl(215_14%_40%)]">{s.hint}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="mt-6 space-y-4">
              {current.fields.map((k) => (
                <label key={k} className="block">
                  <span className={label}>{FIELD_INFO[k].label}</span>
                  <input
                    className={input}
                    value={f[k]}
                    placeholder={FIELD_INFO[k].hint}
                    maxLength={160}
                    onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))}
                  />
                </label>
              ))}
            </div>
            <p className="mt-4 text-[14px] leading-relaxed text-[hsl(215_14%_40%)]">
              Left a box empty? The message shows a [gap in brackets] so you can fill it in later.
            </p>
            <button type="button" onClick={clear} className={`${smallBtn} mt-4`}>
              Clear the boxes
            </button>
          </div>

          {/* The result */}
          <div className="min-w-0 space-y-5" aria-live="polite">
            <MessageCard title="Friendly version" subject={out.subject} body={out.friendly} />
            <MessageCard title="Short version" subject={out.subject} body={out.short} />
            <div className="flex flex-wrap gap-3">
              <CopyButton text={out.subject} what="subject" />
              <button type="button" onClick={download} className={smallBtn}>
                Download as text
              </button>
            </div>

            <div className={card}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-[16px] font-semibold text-[hsl(0_0%_4%)]">Switch it on</p>
                <div role="group" aria-label="Your email app" className="inline-flex rounded-lg border border-[hsl(216_30%_80%)] p-1">
                  {(["gmail", "outlook"] as const).map((a) => (
                    <button
                      key={a}
                      type="button"
                      aria-pressed={app === a}
                      onClick={() => setApp(a)}
                      className={`h-8 rounded-md px-4 text-[14.5px] font-medium transition-colors ${
                        app === a ? "bg-[hsl(0_0%_4%)] text-[hsl(0_0%_100%)]" : "text-[hsl(0_0%_4%)] hover:bg-[hsl(216_60%_97%)]"
                      }`}
                    >
                      {a === "gmail" ? "Gmail" : "Outlook"}
                    </button>
                  ))}
                </div>
              </div>
              {how.clock && (
                <p className="mt-4 rounded-lg bg-[hsl(216_60%_97%)] p-3.5 text-[14.5px] leading-relaxed text-[hsl(215_16%_28%)]">
                  Neither Gmail nor Outlook can switch a reply on and off by the clock every day. These steps keep the reply on all the time, with
                  your hours written in the message, which is what most small teams do. Or switch it on each evening and off each morning.
                </p>
              )}
              {(app === "gmail" ? how.gmail : how.outlook).map((h) => (
                <HowToBlock key={h.title} h={h} />
              ))}
            </div>

            {/* The product, once, after the result (free-tools-plan.md rules) */}
            <div className={card}>
              <p className="text-[16px] font-semibold text-[hsl(0_0%_4%)]">Want this done for you?</p>
              <p className="mt-2 text-[15.5px] leading-relaxed text-[hsl(215_16%_30%)]">
                An auto reply only says “we got it”. ClearMakes Inbox does the next part: posts the request to Slack or Teams, adds it to HubSpot,
                and answers the questions you get every day with your saved answer.
              </p>
              <a href={SIGNUP} className={`${btn.ink} mt-4`}>
                Try Inbox free
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── The page ─────────────────────────────────────────────────────────────── */

const faqs: { q: string; a: ReactNode }[] = [
  {
    q: "What should an auto reply email say?",
    a: "Four things: that the message arrived, when they'll hear back, what to do if it's urgent, and who it's from. Keep it short, give a real time (“within one working day”), and don't promise what you can't keep.",
  },
  {
    q: "Is this auto reply generator really free?",
    a: "Yes. There's no sign-up and no limit. It runs in your browser, and nothing you type is sent to us or anyone else. Copy or download the result and use it however you like.",
  },
  {
    q: "Can Gmail send an auto reply only outside working hours?",
    a: "No. Gmail's vacation responder works by dates, and a template filter replies all the time. Outlook's automatic replies take one start and end time, not a daily schedule. So most teams keep the reply on and write their hours in it.",
  },
  {
    q: "Will an auto reply answer the same person every time?",
    a: "It depends on the method. Gmail's vacation responder replies once, and again after four days. A Gmail template filter replies to every matching email. Outlook rules in classic Outlook reply to every email that matches the rule.",
  },
  {
    q: "Does an auto reply go to newsletters and spam?",
    a: "Gmail's vacation responder skips Spam and mailing lists. Outlook's reply for people outside your organization can reach newsletters and junk; Microsoft suggests sending it only to your contacts instead.",
  },
];

const webAppLd = {
  "@type": "WebApplication",
  name: "Auto Reply Email Generator",
  url: `https://clearmakes.com${PATH}`,
  description: DESC,
  applicationCategory: "BusinessApplication",
  operatingSystem: "Any (runs in the web browser)",
  isAccessibleForFree: true,
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  dateModified: "2026-10-22",
  publisher: { "@type": "Organization", name: "ClearMakes", url: "https://clearmakes.com" },
};

const ToolAutoReplyGenerator = () => (
  <>
    <PageMeta
      title={TITLE}
      description={DESC}
      path={PATH}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Free auto reply generator", path: PATH },
      ]}
    />
    <JsonLd data={webAppLd} />
    <main>
      <Hero
        eyebrow="Free tool · No sign-up"
        title="Free auto reply email generator"
        lead="Write the “we got your message” reply your customers should get, in a friendly and a short version, with the steps to switch it on in Gmail or Outlook."
        primary={{ label: "Write my auto reply", href: "#generator" }}
        updated="22 October 2026"
      />

      <ProblemFirst
        question="Does this happen in your"
        highlight="inbox?"
        problems={[
          "Customers write at night and hear nothing?",
          "People email twice to ask if you got it?",
          "You're away, and nobody knows when you're back?",
          "Every reply you type sounds a bit different?",
        ]}
        promise="One clear reply that goes out by itself."
        line="Fill in a few boxes below. You get the message, and the exact steps to switch it on in your email."
        answers={[
          { title: "Nobody waits in silence", body: "The reply says the message arrived and when they'll hear back." },
          { title: "No second emails", body: "A real time, like “within one working day”, so they don't chase you." },
          { title: "Your return date, clearly", body: "Out of office and holiday replies name the day you're back." },
          { title: "The same words every time", body: "Write it once, set it up once, and every customer gets the same clear reply." },
        ]}
        cta={null}
      />

      <Generator />

      <WhatIs title="What is an auto reply email?">
        An auto reply email is a message your mailbox sends by itself when an email arrives. It tells the sender their message got through, when
        they'll hear back, and what to do if it's urgent. Gmail and Outlook both have one built in: Gmail's vacation responder and template
        filters, and Outlook's automatic replies and rules.
      </WhatIs>

      <Band
        tone="white"
        title="What a good auto reply says"
        answer="Say that the message arrived, when a person will reply, and what to do if it can't wait. Everything else is optional, and shorter is better."
        narrow
      >
        <List
          items={[
            <><strong>It arrived.</strong> “Thanks, we got your message” stops people from sending it twice.</>,
            <><strong>A real time.</strong> “Within one working day” or “by Monday 3 November”, not “as soon as possible”. Pick a time you can keep.</>,
            <><strong>A way out for urgent things.</strong> A phone number or another address, if you have one.</>,
            <><strong>One useful line.</strong> Ask for the order number, or link to your opening hours or prices.</>,
            <><strong>Who it's from.</strong> A name or a team, so it doesn't read like a machine.</>,
          ]}
        />
        <P>
          Writing answers to the questions you get every day, not just “we got it”? The guide to{" "}
          <In href="/guides/auto-reply-to-common-customer-questions">auto-replying to common customer questions</In> has 8 free templates.
        </P>
      </Band>

      <Band
        tone="pale"
        title="How to set up an auto reply in Gmail"
        answer="Gmail has two ways. The vacation responder is for being away between two dates; a template with a filter answers matching emails all the time."
      >
        <div className="max-w-3xl">
          <H3>For being away: the vacation responder</H3>
          <HowToSteps h={GMAIL_VACATION} />
          <H3>For every request: a template and a filter</H3>
          <HowToSteps h={GMAIL_TEMPLATE} />
        </div>
      </Band>

      <Band
        tone="white"
        title="How to set up automatic replies in Outlook"
        answer="Outlook's automatic replies work with Microsoft 365, Exchange and Outlook.com accounts. Set a time period for being away, or leave it on with no end date."
      >
        <div className="max-w-3xl">
          <H3>For being away: automatic replies with a time period</H3>
          <HowToSteps h={OUTLOOK_AUTO} />
          <H3>For every request: automatic replies left on, or a rule</H3>
          <HowToSteps h={OUTLOOK_ALWAYS} />
        </div>
      </Band>

      <Band
        tone="pale"
        title="After the auto reply: the next step"
        answer="An auto reply tells the customer you have their message. Someone still has to see it, pass it on and answer it."
        narrow
      >
        <P>
          That is what <In href="/products/inbox">ClearMakes Inbox</In> does. It can post each new request to the right{" "}
          <In href="/integrations/gmail-to-slack">Slack channel</In> or Teams channel, add the sender to HubSpot, and answer everyday questions with
          your saved answer (<In href="/smart-faq">Smart FAQ</In>). You describe each workflow in one sentence. The free plan has 3 apps, 2 new
          workflows and 100 runs a month.
        </P>
      </Band>

      <Faqs items={faqs} />

      <Band tone="white" title="Sources" narrow>
        <P>
          The steps were checked on 22 October 2026 against Google's and Microsoft's help pages:{" "}
          <Src href="https://support.google.com/mail/answer/25922">Gmail, set up an out of office reply</Src>,{" "}
          <Src href="https://support.google.com/mail/answer/14864208">Gmail, message templates</Src>,{" "}
          <Src href="https://support.microsoft.com/en-us/office/send-automatic-out-of-office-replies-from-outlook-9742f476-5348-4f9f-997f-5e208513bd67">
            Outlook, send automatic out of office replies
          </Src>
          . Menus move now and then; if a name doesn't match, those pages have the newest wording.
        </P>
      </Band>

      <Related
        links={[
          { href: "/guides/auto-reply-to-common-customer-questions", label: "Auto-reply to common questions", desc: "Answer the questions you get every day, with 8 templates." },
          { href: "/smart-faq", label: "Smart FAQ", desc: "Saved answers that go out when the same question comes in." },
          { href: "/integrations/gmail-to-slack", label: "Connect Gmail to Slack", desc: "Four ways, with the steps for each." },
          { href: "/gmail-integration", label: "Gmail automation", desc: "What a workflow can do when an email arrives in Gmail." },
          { href: "/outlook-integration", label: "Outlook automation", desc: "The same for Outlook and Microsoft 365." },
          { href: "/guides", label: "All guides", desc: "Every ClearMakes Inbox guide in one place." },
        ]}
      />

      <Closing
        title="Got the reply? Now handle the request."
        line="ClearMakes Inbox posts it to Slack or Teams, adds it to HubSpot, and answers everyday questions."
        primary={{ label: "Try Inbox free", href: SIGNUP }}
      />
    </main>
    <Footer />
  </>
);

export default ToolAutoReplyGenerator;
